import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import HomeworkAnnotationViewer from '../../components/homework/HomeworkAnnotationViewer'
import HtmlWithMermaid from '../../components/learning/HtmlWithMermaid'
import {
  HomeworkApi,
  HomeworkComment,
  HomeworkSubmission,
  HomeworkTocItem,
  parseAnchorJson,
} from '../../services/homeworkApi'
import { scrollToHomeworkHeading } from '../../utils/homeworkToc'

type SideTab = 'comments' | 'assignment'

function sourceBadge(c: HomeworkComment) {
  if (c.source === 'ai') {
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-violet-100 text-violet-800">
        AI
      </span>
    )
  }
  if (c.source === 'student') {
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-sky-100 text-sky-800">
        Học viên
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800">
      Admin
    </span>
  )
}

function resolveBadge(c: HomeworkComment, studentUserId?: number) {
  if (c.status !== 'resolved') {
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
        Chưa giải quyết
      </span>
    )
  }
  const byStudent =
    c.resolvedBy != null && studentUserId != null && Number(c.resolvedBy) === Number(studentUserId)
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border ${
        byStudent
          ? 'bg-green-50 text-green-800 border-green-200'
          : 'bg-slate-100 text-slate-700 border-slate-200'
      }`}
    >
      {byStudent
        ? 'HV đã giải quyết'
        : c.resolvedByName
          ? `Resolve bởi ${c.resolvedByName}`
          : 'Đã giải quyết'}
    </span>
  )
}

export default function AdminHomeworkSubmissionDetailPage() {
  const { id: courseId, homeworkId, submissionId } = useParams()
  const [data, setData] = useState<HomeworkSubmission | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeCommentId, setActiveCommentId] = useState<number | null>(null)
  const [replyDraft, setReplyDraft] = useState('')
  const [replyParentId, setReplyParentId] = useState<number | null>(null)
  const [sideTab, setSideTab] = useState<SideTab>('comments')
  const [cmtFilter, setCmtFilter] = useState<'all' | 'open' | 'resolved' | 'ai' | 'admin'>('all')
  const [tocCollapsed, setTocCollapsed] = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  const [finalNote, setFinalNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!submissionId) return
    try {
      if (!opts?.silent) setLoading(true)
      const d = await HomeworkApi.getSubmission(Number(submissionId))
      setData(d)
      if (!opts?.silent) setFinalNote(d.adminFinalNote || '')
      else setFinalNote((prev) => (prev === (d.adminFinalNote || '') ? prev : d.adminFinalNote || ''))
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được bài làm')
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }, [submissionId])

  const softReload = useCallback(() => load({ silent: true }), [load])

  useEffect(() => {
    void load()
  }, [load])

  const comments = data?.comments || []
  const toc = (data?.tocJson || []) as HomeworkTocItem[]
  const roots = useMemo(() => comments.filter((c) => !c.parentId), [comments])
  const repliesOf = (id: number) => comments.filter((c) => c.parentId === id)

  const filteredRoots = useMemo(() => {
    return roots.filter((c) => {
      if (cmtFilter === 'open') return c.status === 'open'
      if (cmtFilter === 'resolved') return c.status === 'resolved'
      if (cmtFilter === 'ai') return c.source === 'ai'
      if (cmtFilter === 'admin') return c.source === 'admin'
      return true
    })
  }, [roots, cmtFilter])

  const stats = useMemo(() => {
    const open = roots.filter((c) => c.status === 'open').length
    const resolved = roots.filter((c) => c.status === 'resolved').length
    const byStudent = roots.filter(
      (c) =>
        c.status === 'resolved' &&
        c.resolvedBy != null &&
        Number(c.resolvedBy) === Number(data?.userId)
    ).length
    const ai = roots.filter((c) => c.source === 'ai').length
    const admin = roots.filter((c) => c.source === 'admin').length
    return { open, resolved, byStudent, ai, admin, total: roots.length }
  }, [roots, data?.userId])

  const createText = async (payload: {
    quote: string
    prefix: string
    suffix: string
    body: string
  }) => {
    if (!submissionId) return
    await HomeworkApi.addAdminComment(Number(submissionId), {
      body: payload.body,
      anchorType: 'text',
      anchorJson: {
        quote: payload.quote,
        prefix: payload.prefix,
        suffix: payload.suffix,
      },
    })
    toast.success('Đã thêm comment')
    setSideTab('comments')
    await softReload()
  }

  const createImage = async (payload: {
    imageSrc: string
    xPct: number
    yPct: number
    wPct: number
    hPct: number
    body: string
  }) => {
    if (!submissionId) return
    await HomeworkApi.addAdminComment(Number(submissionId), {
      body: payload.body,
      anchorType: 'image',
      anchorJson: {
        imageSrc: payload.imageSrc,
        xPct: payload.xPct,
        yPct: payload.yPct,
        wPct: payload.wPct,
        hPct: payload.hPct,
      },
    })
    toast.success('Đã thêm comment ảnh')
    setSideTab('comments')
    await softReload()
  }

  const sendReply = async () => {
    if (!submissionId || !replyParentId || !replyDraft.trim()) return
    await HomeworkApi.addAdminComment(Number(submissionId), {
      body: replyDraft.trim(),
      parentId: replyParentId,
    })
    setReplyDraft('')
    setReplyParentId(null)
    await softReload()
  }

  const jumpToc = (id: string) => {
    const item = toc.find((t) => t.id === id)
    const ok = scrollToHomeworkHeading(id, {
      text: item?.text,
      rootSelector: '.homework-annotation-body',
    })
    if (!ok) {
      // retry shortly in case DOM just painted
      window.setTimeout(() => {
        scrollToHomeworkHeading(id, {
          text: item?.text,
          rootSelector: '.homework-annotation-body',
        })
      }, 80)
    }
  }

  if (loading || !data) {
    return <div className="p-8 text-m2-on-surface-variant">Đang tải...</div>
  }

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto">
      <Link
        to={`/admin/courses/${courseId}/homeworks/${homeworkId}/submissions`}
        className="text-sm text-m2-primary hover:underline"
      >
        ← Danh sách bài làm
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3 mt-2 mb-4">
        <div>
          <h1 className="text-xl font-bold">{data.homeworkName}</h1>
          <p className="text-sm text-m2-on-surface-variant">
            {data.studentName} · {data.status} · AI: {data.aiScanStatus}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border"
            onClick={async () => {
              await HomeworkApi.updateSubmissionStatus(data.id, 'complete', finalNote)
              toast.success('Đã hoàn thành')
              await load()
            }}
          >
            Hoàn thành
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border"
            onClick={async () => {
              await HomeworkApi.updateSubmissionStatus(data.id, 'return')
              toast.success('Đã trả bài — học viên có thể sửa')
              await load()
            }}
          >
            Trả bài
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border border-violet-300 text-violet-700"
            onClick={async () => {
              await HomeworkApi.queueAiRescan(data.id)
              toast.success('Đã xếp hàng AI')
              await load()
            }}
          >
            Xếp hàng AI lại
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-m2-outline-variant bg-m2-surface-low p-3">
        <label className="block text-sm font-medium mb-1">Nhận xét cuối (hiện trên list BTVN học viên)</label>
        <textarea
          className="w-full border rounded-md px-3 py-2 text-sm bg-white"
          rows={2}
          value={finalNote}
          onChange={(e) => setFinalNote(e.target.value)}
          placeholder="Tóm tắt đánh giá / hướng dẫn tiếp theo cho học viên..."
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            disabled={savingNote}
            className="px-3 py-1.5 text-sm rounded-md bg-m2-primary text-white disabled:opacity-50"
            onClick={async () => {
              try {
                setSavingNote(true)
                await HomeworkApi.updateFinalNote(data.id, finalNote.trim() || null)
                toast.success('Đã lưu nhận xét cuối')
                await load()
              } catch (e: any) {
                toast.error(e?.message || 'Lưu thất bại')
              } finally {
                setSavingNote(false)
              }
            }}
          >
            {savingNote ? 'Đang lưu…' : 'Lưu nhận xét'}
          </button>
        </div>
      </div>

      <div
        className={`grid grid-cols-1 gap-4 items-start ${
          tocCollapsed && rightCollapsed
            ? 'lg:grid-cols-[44px_minmax(0,1fr)_44px]'
            : tocCollapsed
              ? 'lg:grid-cols-[44px_minmax(0,1fr)_360px]'
              : rightCollapsed
                ? 'lg:grid-cols-[240px_minmax(0,1fr)_44px]'
                : 'lg:grid-cols-[240px_minmax(0,1fr)_360px]'
        }`}
      >
        {/* Left: TOC */}
        {tocCollapsed ? (
          <aside className="hidden lg:flex sticky top-16 h-[calc(100vh-6rem)]">
            <button
              type="button"
              onClick={() => setTocCollapsed(false)}
              className="w-11 border rounded-xl bg-m2-surface-low hover:bg-m2-primary/10 text-m2-on-surface-variant text-xs writing-mode-vertical flex items-center justify-center gap-2 px-1"
              title="Mở mục lục"
              style={{ writingMode: 'vertical-rl' }}
            >
              Mục lục ▸
            </button>
          </aside>
        ) : (
          <aside className="border rounded-xl p-3 bg-m2-surface-low sticky top-16 max-h-[calc(100vh-6rem)] overflow-auto">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="font-semibold text-sm">Mục lục</h3>
              <button
                type="button"
                onClick={() => setTocCollapsed(true)}
                className="text-xs px-2 py-1 rounded border border-m2-outline-variant hover:bg-white text-m2-on-surface-variant"
                title="Thu hẹp mục lục"
              >
                ◂ Thu hẹp
              </button>
            </div>
            {toc.length === 0 ? (
              <p className="text-xs text-m2-on-surface-variant">
                Chưa có heading trong bài. HV nên dùng Heading 1/2/3 khi viết.
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {toc.map((t) => (
                  <li key={t.id} style={{ paddingLeft: `${Math.max(0, t.level - 1) * 10}px` }}>
                    <button
                      type="button"
                      className="text-left text-m2-primary hover:underline w-full truncate"
                      title={t.text}
                      onClick={() => jumpToc(t.id)}
                    >
                      {t.text}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}

        {/* Center: submission body */}
        <div className="border rounded-xl p-4 bg-white min-h-[60vh]">
          <div className="flex lg:hidden gap-2 mb-3">
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border"
              onClick={() => setTocCollapsed((v) => !v)}
            >
              {tocCollapsed ? 'Hiện mục lục' : 'Ẩn mục lục'}
            </button>
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border"
              onClick={() => setRightCollapsed((v) => !v)}
            >
              {rightCollapsed ? 'Hiện panel phải' : 'Ẩn panel phải'}
            </button>
          </div>
          <HomeworkAnnotationViewer
            html={data.contentHtml || ''}
            toc={toc}
            comments={comments}
            mode="annotate"
            hideToc
            activeCommentId={activeCommentId}
            onSelectComment={(id) => {
              setActiveCommentId(id)
              setSideTab('comments')
              setRightCollapsed(false)
            }}
            onCreateTextComment={createText}
            onCreateImageComment={createImage}
            canReply
            onReplyToComment={async (parentId, body) => {
              await HomeworkApi.addAdminComment(Number(submissionId), {
                body,
                parentId,
              })
              toast.success('Đã gửi reply')
              await softReload()
            }}
          />
        </div>

        {/* Right: tabs */}
        {rightCollapsed ? (
          <aside className="hidden lg:flex sticky top-16 h-[calc(100vh-6rem)] justify-end">
            <button
              type="button"
              onClick={() => setRightCollapsed(false)}
              className="w-11 border rounded-xl bg-m2-surface-low hover:bg-m2-primary/10 text-m2-on-surface-variant text-xs flex items-center justify-center px-1"
              title="Mở Comments / Đề bài"
              style={{ writingMode: 'vertical-rl' }}
            >
              ◂ Comments
            </button>
          </aside>
        ) : (
          <aside className="border rounded-xl bg-m2-surface-low sticky top-16 max-h-[calc(100vh-6rem)] flex flex-col overflow-hidden">
            <div className="flex items-center border-b border-m2-outline-variant shrink-0">
              <button
                type="button"
                onClick={() => setSideTab('comments')}
                className={`flex-1 py-2.5 text-sm font-medium border-b-2 ${
                  sideTab === 'comments'
                    ? 'border-m2-primary text-m2-primary'
                    : 'border-transparent text-m2-on-surface-variant'
                }`}
              >
                Comments ({stats.total})
              </button>
              <button
                type="button"
                onClick={() => setSideTab('assignment')}
                className={`flex-1 py-2.5 text-sm font-medium border-b-2 ${
                  sideTab === 'assignment'
                    ? 'border-m2-primary text-m2-primary'
                    : 'border-transparent text-m2-on-surface-variant'
                }`}
              >
                Đề bài
              </button>
              <button
                type="button"
                onClick={() => setRightCollapsed(true)}
                className="hidden lg:inline-flex px-2 py-2 text-xs text-m2-on-surface-variant hover:text-m2-on-surface"
                title="Thu hẹp panel"
              >
                ▸
              </button>
            </div>

          {sideTab === 'assignment' ? (
            <div className="p-3 overflow-auto">
              <HtmlWithMermaid
                className="prose prose-sm max-w-none html-media-center"
                html={data.assignmentHtml || ''}
                emptyHtml="<p class='text-m2-on-surface-variant italic'>Chưa có đề</p>"
              />
            </div>
          ) : (
            <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <div className="px-3 pt-3 pb-2 space-y-2 shrink-0 border-b border-m2-outline-variant/60">
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  <span className="rounded bg-white border px-1.5 py-0.5">
                    Mở: <strong>{stats.open}</strong>
                  </span>
                  <span className="rounded bg-white border px-1.5 py-0.5">
                    Đã resolve: <strong>{stats.resolved}</strong>
                  </span>
                  <span className="rounded bg-green-50 border border-green-200 px-1.5 py-0.5 text-green-800">
                    HV resolve: <strong>{stats.byStudent}</strong>
                  </span>
                  <span className="rounded bg-violet-50 border border-violet-200 px-1.5 py-0.5 text-violet-800">
                    AI: <strong>{stats.ai}</strong>
                  </span>
                  <span className="rounded bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-emerald-800">
                    Admin: <strong>{stats.admin}</strong>
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(
                    [
                      ['all', 'Tất cả'],
                      ['open', 'Chưa xong'],
                      ['resolved', 'Đã resolve'],
                      ['ai', 'AI'],
                      ['admin', 'Admin'],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCmtFilter(key)}
                      className={`px-2 py-0.5 rounded-full text-[11px] border ${
                        cmtFilter === key
                          ? 'bg-m2-primary text-white border-m2-primary'
                          : 'bg-white border-m2-outline-variant'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <ul className="space-y-2 p-3 overflow-auto flex-1">
                {filteredRoots.length === 0 ? (
                  <li className="text-xs text-m2-on-surface-variant text-center py-6">
                    Không có comment phù hợp bộ lọc.
                  </li>
                ) : (
                  filteredRoots.map((c) => {
                    const anchor = parseAnchorJson(c.anchorJson)
                    return (
                      <li
                        key={c.id}
                        className={`rounded-lg border p-2.5 text-sm bg-white ${
                          activeCommentId === c.id
                            ? 'border-amber-400 ring-1 ring-amber-300'
                            : 'border-m2-outline-variant'
                        }`}
                      >
                        <button
                          type="button"
                          className="text-left w-full"
                          onClick={() => setActiveCommentId(c.id)}
                        >
                          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                            {sourceBadge(c)}
                            {resolveBadge(c, data.userId)}
                            {c.visibility === 'pending_approval' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                                Chờ duyệt
                              </span>
                            )}
                            {c.visibility === 'rejected' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                                Đã từ chối
                              </span>
                            )}
                          </div>
                          <p className="text-m2-on-surface leading-snug">{c.body}</p>
                          {c.anchorType === 'text' &&
                            anchor &&
                            'quote' in anchor &&
                            anchor.quote && (
                              <p className="mt-1 text-xs italic text-m2-on-surface-variant truncate">
                                “{anchor.quote}”
                              </p>
                            )}
                          {c.anchorType === 'image' && (
                            <p className="mt-1 text-xs text-m2-on-surface-variant">Neo trên ảnh</p>
                          )}
                          <p className="mt-1 text-[10px] text-m2-on-surface-variant">
                            {c.source === 'ai'
                              ? 'Nguồn: AI'
                              : c.source === 'admin'
                                ? `Nguồn: Admin${c.authorName ? ` (${c.authorName})` : ''}`
                                : `Nguồn: Học viên${c.authorName ? ` (${c.authorName})` : ''}`}
                          </p>
                        </button>

                        {c.source === 'ai' && c.visibility === 'pending_approval' && (
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              className="text-xs px-2 py-1 rounded bg-green-100 text-green-800"
                              onClick={async () => {
                                await HomeworkApi.approveComment(c.id, 'approve')
                                await softReload()
                              }}
                            >
                              Duyệt
                            </button>
                            <button
                              type="button"
                              className="text-xs px-2 py-1 rounded bg-red-100 text-red-800"
                              onClick={async () => {
                                await HomeworkApi.approveComment(c.id, 'reject')
                                await softReload()
                              }}
                            >
                              Từ chối
                            </button>
                          </div>
                        )}

                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            className="text-xs text-m2-primary"
                            onClick={() => setReplyParentId(c.id)}
                          >
                            Reply
                          </button>
                          <button
                            type="button"
                            className="text-xs"
                            onClick={async () => {
                              await HomeworkApi.resolveCommentAdmin(
                                c.id,
                                c.status === 'resolved' ? 'open' : 'resolved'
                              )
                              await softReload()
                            }}
                          >
                            {c.status === 'resolved' ? 'Mở lại' : 'Resolve'}
                          </button>
                        </div>

                        {repliesOf(c.id).map((r) => (
                          <div key={r.id} className="mt-2 ml-2 border-l pl-2 text-xs space-y-0.5">
                            <div className="flex items-center gap-1">{sourceBadge(r)}</div>
                            <p>
                              <span className="text-m2-on-surface-variant">
                                {r.authorName || r.source}:{' '}
                              </span>
                              {r.body}
                            </p>
                          </div>
                        ))}

                        {replyParentId === c.id && (
                          <div className="mt-2">
                            <textarea
                              className="w-full border rounded p-1 text-xs"
                              rows={2}
                              value={replyDraft}
                              onChange={(e) => setReplyDraft(e.target.value)}
                            />
                            <button
                              type="button"
                              className="mt-1 text-xs px-2 py-1 bg-m2-primary text-white rounded"
                              onClick={() => void sendReply()}
                            >
                              Gửi reply
                            </button>
                          </div>
                        )}
                      </li>
                    )
                  })
                )}
              </ul>
            </div>
          )}
          </aside>
        )}
      </div>
    </div>
  )
}
